/**
 * Arquitectura Estratégica — interaction layer.
 *
 * Vanilla ES module, no jQuery. The interactive blocks on this template are the
 * three mobile carousels (Problema cards, Antes/Después panels, Assessment
 * numbered bullets) — see parts/arquitectura-estrategica/{problema,impacto,
 * assessment}.php and src/css/components/arquitectura-estrategica/carousel.css.
 *
 * Each [data-ae-carousel] track is a flex row clipped by the viewport
 * (overflow:hidden); sliding is done by setting --ae-carousel-offset
 * (translateX) on the track — never native scroll, so the viewport always pins
 * to the .wrapper column and nothing overflows the device. Cards-per-view comes
 * from the CSS custom prop --ae-carousel-per-view so JS and CSS agree on slide
 * width and page count. At the breakpoints where the block becomes a static
 * grid (Problema/Impacto ≥834, Assessment ≥1200) the CSS neutralises the track
 * transform and hides the controls; the JS stays a harmless no-op there.
 *
 * Everything degrades cleanly: missing elements → no-op.
 */

/* -------------------------------------------------------------------------- */
/* Carousel                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Wire a single [data-ae-carousel].
 *
 * @param {HTMLElement} carousel
 */
function initCarousel(carousel) {
  const track = carousel.querySelector('[data-ae-carousel-track]');
  const dotsList = carousel.querySelector('[data-ae-carousel-dots]');
  const prevBtn = carousel.querySelector('[data-ae-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-ae-carousel-next]');

  if (!track) {
    return;
  }

  const slides = Array.from(track.querySelectorAll('.ae-carousel__slide'));
  if (!slides.length) {
    return;
  }

  let activePage = 0;
  let dots = [];

  const perView = () => {
    const raw = parseInt(getComputedStyle(carousel).getPropertyValue('--ae-carousel-per-view'), 10);
    return raw > 0 ? raw : 1;
  };

  const pageCount = () => Math.max(1, Math.ceil(slides.length / perView()));

  const gapPx = () => {
    const raw = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap);
    return Number.isFinite(raw) ? raw : 0;
  };

  const render = () => {
    const pages = pageCount();
    activePage = Math.min(activePage, pages - 1);

    const n = perView();
    const slideWidth = slides[0].getBoundingClientRect().width;
    const stride = slideWidth + gapPx();
    track.style.setProperty('--ae-carousel-offset', `${-activePage * n * stride}px`);

    carousel.toggleAttribute('data-ae-carousel-single', pages <= 1);

    dots.forEach((dot, i) => {
      const isActive = i === activePage;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (prevBtn) {
      prevBtn.disabled = activePage <= 0;
    }
    if (nextBtn) {
      nextBtn.disabled = activePage >= pages - 1;
    }
  };

  const goTo = (page) => {
    activePage = Math.max(0, Math.min(page, pageCount() - 1));
    render();
  };

  const buildDots = () => {
    if (!dotsList) {
      dots = [];
      return;
    }
    dotsList.textContent = '';
    dots = [];
    const pages = pageCount();
    for (let i = 0; i < pages; i += 1) {
      const item = document.createElement('li');
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ae-carousel__dot slides__dot';
      dot.setAttribute('aria-label', `Ir al grupo ${i + 1}`);
      dot.addEventListener('click', () => goTo(i));
      item.appendChild(dot);
      dotsList.appendChild(item);
      dots.push(dot);
    }
  };

  if (prevBtn) {
    prevBtn.addEventListener('click', () => goTo(activePage - 1));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => goTo(activePage + 1));
  }

  let lastPerView = perView();
  window.addEventListener('resize', () => {
    const next = perView();
    if (next !== lastPerView) {
      lastPerView = next;
      buildDots();
    }
    render();
  });

  /* ---- Drag / swipe (Pointer Events: mouse + touch + pen) --------------------
     Mirrors the gm-casos carousel (src/js/growth-marketing.js). One pointer flow
     drags the same translateX the controls drive. It is gated to carousel mode
     only: at the grid breakpoints (problema/impacto ≥834, assessment ≥1200) the
     track is a CSS grid (transform:none) and dragging it would do nothing
     visible, so we bail when display is 'grid'. The active page / dots / arrows
     are updated through the existing goTo()/render() so there is one source of
     truth — drag just decides the target page from the gesture distance. */
  const DRAG_THRESHOLD_RATIO = 0.2; // 20% of a slide's width…
  const DRAG_THRESHOLD_MIN = 50; // …but at least 50px.

  let dragging = false;
  let dragPointerId = null;
  let dragStartX = 0;
  let dragBaseOffset = 0; // translateX (px) of the active page when the drag began.
  let dragSlideWidth = 0;

  const isCarouselMode = () => getComputedStyle(track).display !== 'grid';

  const currentOffsetPx = () => {
    const n = perView();
    const slideWidth = slides[0].getBoundingClientRect().width;
    const stride = slideWidth + gapPx();
    return -activePage * n * stride;
  };

  const onPointerDown = (event) => {
    // Only primary button / touch / pen, and only while the carousel is live.
    if (event.button != null && event.button !== 0) {
      return;
    }
    if (!isCarouselMode()) {
      return;
    }
    dragging = true;
    dragPointerId = event.pointerId;
    dragStartX = event.clientX;
    dragBaseOffset = currentOffsetPx();
    dragSlideWidth = slides[0].getBoundingClientRect().width || 1;
    track.classList.add('is-dragging'); // CSS kills the transition + sets grabbing cursor.
    if (track.setPointerCapture) {
      try {
        track.setPointerCapture(event.pointerId);
      } catch (e) {
        /* setPointerCapture can throw if the pointer is already gone — ignore. */
      }
    }
  };

  const onPointerMove = (event) => {
    if (!dragging || event.pointerId !== dragPointerId) {
      return;
    }
    const delta = event.clientX - dragStartX;
    // Follow the finger/cursor live; the transition is suppressed via .is-dragging.
    track.style.setProperty('--ae-carousel-offset', `${dragBaseOffset + delta}px`);
  };

  const endDrag = (event) => {
    if (!dragging || (event && event.pointerId !== dragPointerId)) {
      return;
    }
    dragging = false;
    track.classList.remove('is-dragging'); // re-enables the transition for the snap.
    if (track.releasePointerCapture && dragPointerId != null) {
      try {
        track.releasePointerCapture(dragPointerId);
      } catch (e) {
        /* ignore */
      }
    }

    const delta = event ? event.clientX - dragStartX : 0;
    const threshold = Math.max(DRAG_THRESHOLD_MIN, dragSlideWidth * DRAG_THRESHOLD_RATIO);
    dragPointerId = null;

    if (delta <= -threshold) {
      goTo(activePage + 1); // dragged left → next page (render() snaps).
    } else if (delta >= threshold) {
      goTo(activePage - 1); // dragged right → prev page.
    } else {
      render(); // below threshold → snap back to the current page.
    }
  };

  track.addEventListener('pointerdown', onPointerDown);
  track.addEventListener('pointermove', onPointerMove);
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  // Block native image drag so the gesture never starts a file/ghost drag.
  track.addEventListener('dragstart', (event) => event.preventDefault());

  buildDots();
  render();
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                  */
/* -------------------------------------------------------------------------- */

function init() {
  document.querySelectorAll('[data-ae-carousel]').forEach(initCarousel);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
